import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "YOUR_ACCESS_KEY_HERE";

const SERVICE_OPTIONS = [
  "Diagnostic / Won't Start",
  "Brakes & Suspension",
  "Battery / Alternator",
  "Scheduled Maintenance",
  "Other / Rescue Service",
];

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_address: "",
    vehicle_info: "",
    service_type: "",
    message: "",
    contact_method: "Phone",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: "New Car Repair Inquiry from " + formData.name,
        from_name: "Protech Mobile Mechanic LLC",
        ...formData,
      };

      await axios.post("https://api.web3forms.com/submit", payload);
      setStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_address: "",
        vehicle_info: "",
        service_type: "",
        message: "",
        contact_method: "Phone",
      });
    } catch (error) {
      console.error("Web3Forms Submission Error:", error);
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="uppercase text-xs tracking-[0.2em] text-muted-foreground font-medium mb-4"
        >
          Get In Touch
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none mb-16 max-w-xl"
        >
          Stuck in your driveway? <span className="text-primary">Request a Quote.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {status === "success" ? (
              <div className="bg-card border border-stone-200 rounded-xl p-10 text-center" data-testid="contact-success">
                <CheckCircle weight="duotone" className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-2xl mb-2">Message Sent!</h3>
                <p className="text-muted-foreground mb-6">
                  We'll get back to you as soon as possible. You can also call us directly for faster service.
                </p>
                <Button
                  onClick={() => setStatus("idle")}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="contact-send-another"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium mb-2 block">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      required
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_address" className="text-sm font-medium mb-2 block">
                      Service Address *
                    </Label>
                    <Input
                      id="service_address"
                      required
                      placeholder="Where is the vehicle?"
                      value={formData.service_address}
                      onChange={(e) => handleChange("service_address", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                      data-testid="contact-address-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      placeholder="you@email.com"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      placeholder="(503) 380-5413"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="vehicle_info" className="text-sm font-medium mb-2 block">
                      Vehicle (Year/Make/Model) *
                    </Label>
                    <Input
                      id="vehicle_info"
                      required
                      placeholder="e.g. 2018 Honda Civic"
                      value={formData.vehicle_info}
                      onChange={(e) => handleChange("vehicle_info", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                      data-testid="contact-vehicle-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service" className="text-sm font-medium mb-2 block">
                      Service Needed *
                    </Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(val) => handleChange("service_type", val)}
                    >
                      <SelectTrigger
                        className="bg-card border-stone-200 rounded-xl h-11"
                        data-testid="contact-service-select"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent data-testid="contact-service-dropdown">
                        {SERVICE_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact_method" className="text-sm font-medium mb-2 block">
                    Preferred Contact Method
                  </Label>
                  <Select
                    value={formData.contact_method}
                    onValueChange={(val) => handleChange("contact_method", val)}
                  >
                    <SelectTrigger
                      className="bg-card border-stone-200 rounded-xl h-11"
                      data-testid="contact-method-select"
                    >
                      <SelectValue placeholder="Preferred Contact Method" />
                    </SelectTrigger>
                    <SelectContent data-testid="contact-method-dropdown">
                      <SelectItem value="Phone">Phone</SelectItem>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="Text">Text</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    required
                    rows={5}
                    placeholder="Describe the issue... (e.g. won't crank, brake noise, etc.)"
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    className="bg-card border-stone-200 focus:ring-primary rounded-xl"
                    data-testid="contact-message-input"
                  />
                </div>

                {status === "error" && (
                  <p className="text-red-500 text-sm" data-testid="contact-error">
                    Something went wrong. Please try again or call us directly.
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={status === "loading"}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] transition-transform font-heading font-bold text-base px-8 h-12 w-full sm:w-auto"
                  data-testid="contact-submit-button"
                >
                  {status === "loading" ? (
                    "Sending..."
                  ) : (
                    <>
                      <PaperPlaneTilt weight="bold" className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>

          {/* Map & Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="space-y-6"
          >
            {/* Google Map */}
            <div
              className="rounded-xl overflow-hidden border border-stone-200 h-[280px] lg:h-[320px]"
              data-testid="contact-map"
            >
              <iframe
                title="Protech Mobile Mechanic Location"
                src="https://www.google.com/maps?q=2056+NE+Aloclek+Dr+Ste+310,+Hillsboro,+OR+97124&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-card border border-stone-200 rounded-xl p-5" data-testid="contact-info-address">
                <MapPin weight="duotone" className="w-5 h-5 text-primary mb-3" />
                <p className="font-heading font-bold text-sm mb-1">Address</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  2056 NE Aloclek Dr Ste 310,
                  <br />
                  Hillsboro, OR 97124
                </p>
              </div>
              <div className="bg-card border border-stone-200 rounded-xl p-5" data-testid="contact-info-phone">
                <Phone weight="duotone" className="w-5 h-5 text-primary mb-3" />
                <p className="font-heading font-bold text-sm mb-1">Phone</p>
                <a
                  href="tel:+15033805413"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors duration-200"
                >
                  (503) 380-5413
                </a>
              </div>
              <div className="bg-card border border-stone-200 rounded-xl p-5" data-testid="contact-info-hours">
                <Clock weight="duotone" className="w-5 h-5 text-primary mb-3" />
                <p className="font-heading font-bold text-sm mb-1">Hours</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Mon–Sat: 10am–6pm
                  <br />
                  Sun: Closed
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
